<script lang="ts" setup>
	const { query } = useRoute()
	const { back } = useRouter()

	const { toLower, urlQuery } = useUtils()
	const currentRoute = computed(() => {
		const { name } = useRoute()
		return toLower(String(name))
	})
	const routePath = computed(() => {
		return { path: `${import.meta.env.VITE_INDIVIDUAL_URL}/register`, query }
	})

	const platformList = shallowRef([
		{
			key: 'business',
			label: 'Business',
			icon: 'briefcase',
			src: '/register',
			description: 'Get cover for your business'
		},
		{
			key: 'individual',
			label: 'Individual',
			icon: 'persons',
			external: true,
			src: `${import.meta.env.VITE_INDIVIDUAL_URL}/register`,
			description: 'Get cover for yourself & your loved ones?'
		}
	])
	const platformDropdown = shallowRef(false)
	const hasNOQuery = computed(() => ['register', 'login'].includes(currentRoute.value) && !urlQuery.value?.page)
</script>

<template>
	<div id="auth">
		<header>
			<nav
				class="sticky top-0 z-[1000] w-full bg-gray-50 px-3 py-5 pt-8 md:min-h-[80px] md:border-b md:pt-5 lg:px-0"
				:class="{ 'min-h-[80px] border-b': hasNOQuery }"
			>
				<button v-if="!hasNOQuery" type="button" aria-label="navigation button" class="md:hidden" @click="back">
					<lazy-tools-icon dir="arrows" icon="back-arrow" :height="24" />
				</button>

				<div v-if="hasNOQuery" class="flex items-center gap-2 overflow-hidden md:hidden">
					<div
						class="flexed size-12 justify-center rounded-full border-[0.5px] border-primary-100 bg-primary-50"
					>
						<lazy-tools-icon dir="fill" icon="mcg-cap" :height="48" :width="48" />
					</div>

					<button
						type="button"
						aria-label="account switch"
						class="flex max-w-[199px] items-center justify-between gap-x-2 rounded-[100px] bg-primary-50 p-[10px_16px]"
						@click="platformDropdown = true"
					>
						<span class="text-center text-sm font-semibold text-main">Business Account</span>
						<lazy-tools-icon
							dir="arrows"
							icon="caret-down"
							:height="16"
							:width="15"
							path-class="fill-blue-950"
						/>
					</button>
				</div>
				<div
					class="mx-auto hidden w-full cursor-pointer flex-wrap items-end justify-between gap-y-2 md:flex lg:w-11/12"
				>
					<div class="flex items-end gap-x-4">
						<nuxt-link to="/">
							<img
								src="~/assets/svg/logos/mcg-logo.svg"
								alt="logo"
								loading="lazy"
								height="36.67"
								width="137.5"
								crossorigin="use-credentials"
								class="w-auto"
							/>
						</nuxt-link>
						<div
							class="inline-flex translate-y-2 rounded-3xl bg-primary-50 px-5 py-1.5 font-medium text-primary-2"
						>
							Business
						</div>
					</div>

					<div class="text-sm lg:text-base">
						<span class="text-blueGray-600">Trying to get care for yourself and loved ones?</span>
						<nuxt-link :to="routePath" class="font-medium text-primary-2" target="_blank" external>
							Get covered as an individual
						</nuxt-link>
					</div>
				</div>
			</nav>
		</header>

		<shared-wrapper class="mt-5 p-5">
			<slot />
		</shared-wrapper>

		<dialogs
			v-model:show="platformDropdown"
			btn-position="center"
			centered
			:ui="{
				modal: 'pb-5 px-0  mb-0 overflow-hidden',
				header: 'mb-0',
				title: 'inline'
			}"
		>
			<div class="mx-auto w-full text-[#094163]">
				<shared-title-header
					title="Select your preference"
					class="custom-transition-2 mx-auto bg-primary-50 py-7"
					:ui="{ size: 'text-xl text-center', color: 'text-blue-950', font: 'font-bold' }"
				/>

				<div class="my-8 gap-10 space-y-8 px-5 pb-5">
					<nuxt-link
						v-for="(link, index) in platformList"
						:key="index"
						:to="link.src"
						:target="link?.external ? '_blank' : '_parent'"
						:external="link?.external || false"
						class="custom-transition-2 group relative flex w-full items-center gap-x-2 rounded-[4px] border border-gray-200 p-[16px_12px] hover:border-main hover:bg-primary-50"
						@click="() => (platformDropdown = false)"
					>
						<div
							class="custom-transition-2 flex h-[32px] w-[32px] flex-none items-center justify-center rounded-full bg-gray-50 group-hover:bg-white"
						>
							<lazy-tools-icon
								:icon="link.icon"
								dir="fill"
								:height="18"
								:width="18"
								path-class="fill-gray-300"
								class="group-hover:hidden"
							/>
							<lazy-tools-icon
								:icon="link.icon"
								dir="fill"
								:height="18"
								:width="18"
								class="hidden group-hover:block"
							/>
						</div>

						<div class="w-full space-y-1">
							<h4 class="text-base font-semibold text-gray-400 group-hover:text-blue-950 sm:text-lg">
								For {{ link.label }}
							</h4>
							<p class="text-xs font-medium leading-tight text-gray-600">
								{{ link.description }}
							</p>
						</div>

						<div class="custom-transition-2 animate-x relative opacity-0 group-hover:opacity-100">
							<lazy-tools-icon
								icon="arrow-right"
								dir="arrows"
								path-class="stroke-gray-700 dark:stroke-gray-400"
							/>
						</div>
					</nuxt-link>
				</div>
			</div>
		</dialogs>
	</div>
</template>
